import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { getAllGlobalKeys, toggleGlobalKey } from '~/api/globalKeys';
import { GlobalKey } from '~/types/globalKeys';

type LoadingConfig = {
  flag: boolean;
  comment?: string;
};

const defaultLoadingConfig: LoadingConfig = {
  flag: false,
  comment: ''
};

export default function useGlobalKeys() {
  const [globalKeys, setGlobalKeys] = useState<GlobalKey[]>([]);
  const [loadingConfig, setLoadingConfig] = useState<LoadingConfig>(defaultLoadingConfig);

  useEffect(() => {
    fetchGlobalKeys();
  }, []);

  const fetchGlobalKeys = async (refresh: boolean = false) => {
    setLoadingConfig({
      flag: true,
      comment: refresh ? 'Refreshing global keys...' : 'Fetching global keys...'
    });
    try {
      const { data } = await getAllGlobalKeys();
      setGlobalKeys(data.globalKeys);
      setLoadingConfig({ flag: false });
    } catch (error: any) {
      setLoadingConfig({ flag: false });
      if (error?.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to fetch global keys');
      }
    }
  };

  const toggleKeyFlag = async (uuid: string, currentFlag: boolean) => {
    const newFlag = !currentFlag;
    setLoadingConfig({
      flag: true,
      comment: `${newFlag ? 'Enabling' : 'Disabling'} global key...`
    });
    
    try {
      await toggleGlobalKey(uuid, newFlag);
      
      // Update local state
      setGlobalKeys(prevKeys => 
        prevKeys.map(key => 
          key.uuid === uuid ? { ...key, flag: newFlag } : key
        )
      );
      
      setLoadingConfig({ flag: false });
      toast.success(`Global key ${newFlag ? 'enabled' : 'disabled'} successfully`);
    } catch (error: any) {
      setLoadingConfig({ flag: false });
      if (error?.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update global key');
      }
    }
  };

  return {
    globalKeys,
    loadingConfig,
    toggleKeyFlag,
    refreshGlobalKeys: () => fetchGlobalKeys(true),
  };
}; 